import { datadogRum } from "@datadog/browser-rum";
import { addResumeDebugEvent } from "@/store/useResumeDebugStore";

declare global {
  interface Window {
    __lessonStartupPerfEvents?: Array<Record<string, unknown>>;
  }
}

export type LessonStartupPerfVariant = "optimized" | "baseline";
export type LessonStartupPerfOverride = "default" | LessonStartupPerfVariant;

export const DEFAULT_LESSON_STARTUP_PERF_VARIANT: LessonStartupPerfVariant =
  "optimized";

const STARTUP_ANCHOR_STORAGE_KEY = "lessonStartupPerfAnchor";
const SESSION_STARTUP_ANCHOR_STORAGE_KEY = "sessionStartupPerfAnchor";
const STARTUP_ANCHOR_TTL_MS = 10 * 60 * 1000;
const RESOURCE_TIMING_START_EARLY_TOLERANCE_MS = 5;
// Allow normal browser scheduling delay while keeping repeated startup calls distinct.
const RESOURCE_TIMING_START_LATE_TOLERANCE_MS = 500;

type LessonStartupPerfAnchor = {
  id: string;
  startedAt: number;
  wallClockStartedAtMs: number;
  data: Record<string, unknown>;
};

type ConsumeLessonStartupPerfAnchorOptions = {
  expectedLessonId?: unknown;
};

let activeStartupAnchor: LessonStartupPerfAnchor | null = null;
let activeSessionStartupAnchor: LessonStartupPerfAnchor | null = null;
const wsOpenedAtBySessionId = new Map<string, number>();

export function getLessonStartupPerfNow(): number {
  if (typeof performance !== "undefined" && performance.now) {
    return performance.now();
  }
  return Date.now();
}

function createAnchorId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getActiveStartupAnchor(
  storageKey: string,
): LessonStartupPerfAnchor | null {
  return storageKey === SESSION_STARTUP_ANCHOR_STORAGE_KEY
    ? activeSessionStartupAnchor
    : activeStartupAnchor;
}

function setActiveStartupAnchor(
  storageKey: string,
  anchor: LessonStartupPerfAnchor | null,
): void {
  if (storageKey === SESSION_STARTUP_ANCHOR_STORAGE_KEY) {
    activeSessionStartupAnchor = anchor;
    return;
  }
  activeStartupAnchor = anchor;
}

function safeGetStartupAnchor(storageKey: string): string | null {
  try {
    if (typeof sessionStorage === "undefined") {
      return null;
    }
    return sessionStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function safeSetStartupAnchor(
  storageKey: string,
  anchor: LessonStartupPerfAnchor,
): void {
  try {
    if (typeof sessionStorage === "undefined") {
      return;
    }
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        id: anchor.id,
        wallClockStartedAtMs: anchor.wallClockStartedAtMs,
        data: anchor.data,
      }),
    );
  } catch {
    // Keep the in-memory anchor; analytics must not block lesson startup.
  }
}

function safeClearStartupAnchor(storageKey: string): void {
  try {
    if (typeof sessionStorage === "undefined") {
      return;
    }
    sessionStorage.removeItem(storageKey);
  } catch {
    // Best effort only.
  }
}

function isSameLessonId(left: unknown, right: unknown): boolean {
  if (left == null || right == null) {
    return left === right;
  }
  return String(left) === String(right);
}

function getStoredStartupAnchor(
  storageKey: string,
): LessonStartupPerfAnchor | null {
  const stored = safeGetStartupAnchor(storageKey);
  if (!stored) {
    return getActiveStartupAnchor(storageKey);
  }

  try {
    const parsed = JSON.parse(stored) as Omit<
      LessonStartupPerfAnchor,
      "startedAt"
    >;
    const elapsedMs = Date.now() - Number(parsed.wallClockStartedAtMs);

    if (!Number.isFinite(elapsedMs) || elapsedMs > STARTUP_ANCHOR_TTL_MS) {
      safeClearStartupAnchor(storageKey);
      setActiveStartupAnchor(storageKey, null);
      return null;
    }

    const anchor = {
      ...parsed,
      startedAt: getLessonStartupPerfNow() - elapsedMs,
    };
    setActiveStartupAnchor(storageKey, anchor);
    return anchor;
  } catch {
    safeClearStartupAnchor(storageKey);
    setActiveStartupAnchor(storageKey, null);
    return null;
  }
}

function startStartupPerfAnchor(
  storageKey: string,
  data: Record<string, unknown>,
): void {
  const anchor = {
    id: createAnchorId(),
    startedAt: getLessonStartupPerfNow(),
    wallClockStartedAtMs: Date.now(),
    data,
  };

  setActiveStartupAnchor(storageKey, anchor);
  safeSetStartupAnchor(storageKey, anchor);
}

function clearStartupPerfAnchor(storageKey: string): void {
  setActiveStartupAnchor(storageKey, null);
  safeClearStartupAnchor(storageKey);
}

function consumeStartupPerfAnchor(
  storageKey: string,
  event: string,
  data: Record<string, unknown> = {},
  options: ConsumeLessonStartupPerfAnchorOptions = {},
): boolean {
  const anchor = getStoredStartupAnchor(storageKey);
  if (!anchor) {
    return false;
  }

  const shouldValidateLessonId = Object.prototype.hasOwnProperty.call(
    options,
    "expectedLessonId",
  );

  if (
    shouldValidateLessonId &&
    !isSameLessonId(anchor.data.lesson_id, options.expectedLessonId)
  ) {
    clearStartupPerfAnchor(storageKey);
    return false;
  }

  recordLessonStartupPerf(
    event,
    {
      ...anchor.data,
      ...data,
      startup_anchor_id: anchor.id,
    },
    anchor.startedAt,
  );
  clearStartupPerfAnchor(storageKey);
  return true;
}

export function startLessonStartupPerfAnchor(
  data: Record<string, unknown>,
): void {
  startStartupPerfAnchor(STARTUP_ANCHOR_STORAGE_KEY, data);
}

export function clearLessonStartupPerfAnchor(): void {
  clearStartupPerfAnchor(STARTUP_ANCHOR_STORAGE_KEY);
}

export function startSessionStartupPerfAnchor(
  data: Record<string, unknown>,
): void {
  startStartupPerfAnchor(SESSION_STARTUP_ANCHOR_STORAGE_KEY, data);
}

export function clearSessionStartupPerfAnchor(): void {
  clearStartupPerfAnchor(SESSION_STARTUP_ANCHOR_STORAGE_KEY);
}

export function markLessonStartupWsOpen(
  sessionId: unknown,
  openedAt: number = getLessonStartupPerfNow(),
): void {
  if (sessionId == null) {
    return;
  }

  wsOpenedAtBySessionId.set(String(sessionId), openedAt);
}

export function consumeLessonStartupWsOpenToNowMs(
  sessionId: unknown,
  now: number = getLessonStartupPerfNow(),
): number | null {
  if (sessionId == null) {
    return null;
  }

  const key = String(sessionId);
  const openedAt = wsOpenedAtBySessionId.get(key);
  wsOpenedAtBySessionId.delete(key);

  if (typeof openedAt !== "number") {
    return null;
  }

  return Math.max(0, now - openedAt);
}

export function normalizeLessonStartupPerfVariant(
  value: unknown,
): LessonStartupPerfVariant | null {
  if (value === "optimized" || value === "baseline") {
    return value;
  }
  return null;
}

export function resolveLessonStartupPerfVariant(
  userConfigVariant: unknown,
  debugOverride: unknown,
): LessonStartupPerfVariant {
  const override = normalizeLessonStartupPerfVariant(debugOverride);
  if (override) {
    return override;
  }

  return (
    normalizeLessonStartupPerfVariant(userConfigVariant) ??
    DEFAULT_LESSON_STARTUP_PERF_VARIANT
  );
}

export function recordLessonStartupPerf(
  event: string,
  data: Record<string, unknown> = {},
  startedAt?: number,
): void {
  const now = getLessonStartupPerfNow();
  const payload = {
    ...data,
    ...(typeof startedAt === "number"
      ? { duration_ms: Math.round((now - startedAt) * 10) / 10 }
      : {}),
    measured_at: new Date().toISOString(),
  };

  try {
    addResumeDebugEvent(`perf:${event}`, payload);
  } catch {
    // Best effort only.
  }

  try {
    if (
      typeof window !== "undefined" &&
      Array.isArray(window.__lessonStartupPerfEvents)
    ) {
      window.__lessonStartupPerfEvents.push({
        event,
        ...payload,
      });
    }
  } catch {
    // Best effort only.
  }

  try {
    if (import.meta.env.VITE_DATADOG_ENABLED === "true") {
      datadogRum.addAction("lesson_startup_perf", {
        event,
        ...payload,
      });
    }
  } catch {
    // Best effort only.
  }
}

function roundDurationMs(value: number): number {
  return Math.round(value * 10) / 10;
}

function resourcePath(resourceName: string): string {
  try {
    return new URL(resourceName).pathname;
  } catch {
    return resourceName;
  }
}

export function recordLatestStartupResourceTiming(
  event: string,
  resourcePathPattern: string,
  startedAt: number,
  data: Record<string, unknown> = {},
): boolean {
  try {
    if (
      typeof performance === "undefined" ||
      typeof performance.getEntriesByType !== "function"
    ) {
      return false;
    }

    const earliestStart =
      startedAt - RESOURCE_TIMING_START_EARLY_TOLERANCE_MS;
    const latestStart = startedAt + RESOURCE_TIMING_START_LATE_TOLERANCE_MS;
    const entries = performance
      .getEntriesByType("resource")
      .filter(
        (entry): entry is PerformanceResourceTiming =>
          "initiatorType" in entry &&
          entry.name.includes(resourcePathPattern) &&
          entry.startTime >= earliestStart &&
          entry.startTime <= latestStart,
      )
      .sort(
        (left, right) =>
          Math.abs(left.startTime - startedAt) -
          Math.abs(right.startTime - startedAt),
      );
    const entry = entries[0];
    if (!entry) {
      return false;
    }

    const responseEnd =
      entry.responseEnd || entry.startTime + Math.max(0, entry.duration);
    const fetchStart = entry.fetchStart || entry.startTime;
    const detailedTimingAvailable =
      entry.requestStart > 0 && entry.responseStart > 0;

    recordLessonStartupPerf(event, {
      ...data,
      resource_path: resourcePath(entry.name),
      next_hop_protocol: entry.nextHopProtocol || null,
      resource_duration_ms: roundDurationMs(
        Math.max(0, entry.duration || responseEnd - entry.startTime),
      ),
      resource_total_ms: roundDurationMs(
        Math.max(0, responseEnd - entry.startTime),
      ),
      resource_queue_ms: detailedTimingAvailable
        ? roundDurationMs(Math.max(0, entry.requestStart - fetchStart))
        : null,
      resource_ttfb_ms: detailedTimingAvailable
        ? roundDurationMs(Math.max(0, entry.responseStart - entry.requestStart))
        : null,
      resource_response_download_ms: detailedTimingAvailable
        ? roundDurationMs(Math.max(0, responseEnd - entry.responseStart))
        : null,
      resource_timing_available: detailedTimingAvailable,
    });
    return true;
  } catch {
    return false;
  }
}

export function consumeLessonStartupPerfAnchor(
  event: string,
  data: Record<string, unknown> = {},
  options: ConsumeLessonStartupPerfAnchorOptions = {},
): boolean {
  return consumeStartupPerfAnchor(
    STARTUP_ANCHOR_STORAGE_KEY,
    event,
    data,
    options,
  );
}

export function consumeSessionStartupPerfAnchor(
  event: string,
  data: Record<string, unknown> = {},
  options: ConsumeLessonStartupPerfAnchorOptions = {},
): boolean {
  return consumeStartupPerfAnchor(
    SESSION_STARTUP_ANCHOR_STORAGE_KEY,
    event,
    data,
    options,
  );
}
